/**
 * §14.8 screen 4 — Yangi murojaat: the walk-in at the qabulxona.
 *
 * ===========================================================================
 * THIS SCREEN IS A CONSENT INSTRUMENT THAT HAPPENS TO CONTAIN A FORM.
 * ===========================================================================
 *
 * A citizen is standing at a desk. A staff member types their appeal for them
 * and then asserts, on the record, that they read it back and that the citizen
 * agreed. Everything below exists to make that assertion true rather than
 * merely recorded, and §14.5.4's five steps are implemented in order:
 *
 *  1. THE CITIZEN'S LOCALE IS A FIELD, chosen before anything else, and it is
 *     NOT the panel's language. The consent notice renders in it, at a size
 *     that can be read across a desk. Rendering the notice in the staff
 *     member's Russian while the citizen reads only Uzbek would evidence
 *     consent to a text nobody showed them.
 *  2. THE HASH IS OF THE RENDERED STRING, never of a key name. Hashing
 *     `admin.reception.consent` would prove a placeholder existed; hashing the
 *     sentence proves which sentence was on screen.
 *  3. THE ATTESTATION IS A CHECKBOX WITH A SENTENCE, not a styled button. It
 *     says what the staff member is asserting, in the first person, and the
 *     submit control is dead until it is ticked.
 *  4. (server) the audit row.
 *  5. THE REFERENCE IS THE POINT OF THE WHOLE INTERACTION. After 201 the form
 *     is replaced by the qayd raqami in large type with a print action. The
 *     citizen must not leave without it.
 *
 * ===========================================================================
 * ONE IDEMPOTENCY KEY PER FORM, MINTED WHEN IT OPENS.
 * ===========================================================================
 *
 * A reception desk is a place where somebody clicks submit twice because the
 * network paused. Two appeals for one visit is not a duplicate record — it is
 * a second legal obligation, a second 15-day deadline and a second entry in
 * that citizen's history. The key is minted once per form instance and is
 * regenerated only when the staff member explicitly starts a new appeal.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '@navoiy/api-types';
import {
  AUTHOR_ADDRESS_MAX_CHARS,
  AUTHOR_NAME_MAX_CHARS,
  BODY_MAX_CHARS,
  BODY_MIN_CHARS,
  CONSENT_VERSION,
  LOCALES,
  PHONE_PREFIX,
  normalizePhone,
  type Locale,
} from '@navoiy/domain';
import { ApiError, api, errorKey, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { translate } from '../i18n/index.js';
import { formatDate, formatDateTime } from '../lib/format.js';
import { useMahallas } from '../lib/useMahallas.js';
import {
  Button,
  Card,
  ErrorBanner,
  Field,
  Select,
  TextArea,
  TextInput,
  WarningBanner,
} from '../ui/index.jsx';

type Created = components['schemas']['AdminMurojaatCreated'];

interface Draft {
  mahallaId: string;
  citizenLocale: Locale;
  authorName: string;
  authorAddress: string;
  phone: string;
  body: string;
}

const EMPTY: Draft = {
  mahallaId: '',
  citizenLocale: LOCALES[0],
  authorName: '',
  authorAddress: '',
  phone: '',
  body: '',
};

/**
 * SHA-256 of the rendered consent text, as lowercase hex.
 *
 * `crypto.subtle` requires a secure context — https, or localhost in
 * development. If it is unavailable this screen REFUSES TO SUBMIT rather than
 * sending a placeholder or omitting the field: `consent_text_sha256` is the
 * evidence that a particular notice was displayed, and a wrong one is worse
 * than a missing one because it looks like evidence.
 */
async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function ReceptionScreen(): React.JSX.Element {
  const { t, locale } = useI18n();
  const { expire } = useSession();
  const { mahallas, loading: mahallasLoading } = useMahallas(locale);

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [attested, setAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Created | null>(null);

  /**
   * One key per form instance (§14.5.3). `useState` with an initialiser, not
   * `useMemo`: a memo may be recomputed by React at its discretion, and a new
   * key mid-form is exactly the duplicate this field exists to prevent.
   */
  const [submissionId, setSubmissionId] = useState(() => crypto.randomUUID());

  /** Whether this browser can hash at all — see `sha256Hex`. */
  const canHash = typeof crypto !== 'undefined' && typeof crypto.subtle?.digest === 'function';

  /**
   * THE NOTICE THE CITIZEN IS SHOWN, in the CITIZEN's language.
   *
   * `translate(citizenLocale, …)` rather than the context's `t`, which is bound
   * to the PANEL's language. This one string is the only text on the screen
   * that is deliberately not in the staff member's language, and the whole
   * consent record depends on that being true.
   */
  const consentText = useMemo(
    () => translate(draft.citizenLocale, 'admin.reception.consent'),
    [draft.citizenLocale],
  );

  const set = useCallback(<K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((previous) => ({ ...previous, [key]: value }));
  }, []);

  const normalizedPhone = normalizePhone(draft.phone);
  const bodyLength = draft.body.trim().length;

  /**
   * Every Art. 6 element, present. The server enforces the same rule through
   * the `text_needs_identity` CHECK — this only decides when the button
   * lights up, so a staff member is not told at submit time that a field they
   * never saw was required.
   */
  const complete =
    draft.mahallaId !== '' &&
    draft.authorName.trim().length > 0 &&
    draft.authorAddress.trim().length > 0 &&
    normalizedPhone !== null &&
    bodyLength >= BODY_MIN_CHARS;

  const ready = complete && attested && canHash && !submitting;

  const handle = useCallback(
    (caught: unknown) => {
      if (caught instanceof ApiError && caught.status === 401) {
        expire();
        return;
      }
      if (caught instanceof ApiError) {
        // The three refusals this form can actually provoke get their own
        // sentence. A generic "validation failed" at a reception desk sends a
        // staff member hunting through six fields with a citizen watching.
        /**
         * Matched on the FIELD, not on the code.
         *
         * Two layers can refuse this: the contract's `enum: [true]` answers
         * `{field: 'consent_attested', code: 'enum'}`, and the handler's own
         * check answers `consent_required`. Which one fires depends on how the
         * body was malformed, and the staff member needs the same sentence
         * either way — matching only the handler's code left the ajv refusal
         * falling through to a generic "validation failed" at a desk with a
         * citizen waiting.
         */
        if (caught.fieldCode('consent_attested') !== null) {
          setError(t('admin.reception.errorAttestRequired'));
          return;
        }
        if (caught.fieldCode('consent_version') === 'stale') {
          setError(t('admin.reception.errorConsentStale'));
          return;
        }
        if (caught.code === 'idempotency_conflict') {
          setError(t('admin.reception.errorConflict'));
          return;
        }
      }
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    },
    [expire, t],
  );

  async function submit(): Promise<void> {
    if (!ready || normalizedPhone === null) return;
    setSubmitting(true);
    setError(null);
    try {
      // Step 2 — the hash of what was actually on screen.
      const consentHash = await sha256Hex(consentText);

      const data = unwrap(
        await api().POST('/admin/murojaats', {
          params: { header: { 'Idempotency-Key': submissionId } },
          body: {
            submission_id: submissionId,
            mahalla_id: draft.mahallaId,
            locale: draft.citizenLocale,
            kind: 'text',
            author_name: draft.authorName.trim(),
            author_address: draft.authorAddress.trim(),
            body: draft.body.trim(),
            phone: normalizedPhone,
            consent_attested: true,
            consent_version: CONSENT_VERSION,
            consent_text_sha256: consentHash,
            client: { app: 'admin', version: __APP_VERSION__ },
          },
        }),
      );
      setCreated(data);
    } catch (caught) {
      handle(caught);
    } finally {
      setSubmitting(false);
    }
  }

  /** A NEW appeal means a new key. Nothing else in this file mints one. */
  function startAnother(): void {
    setCreated(null);
    setDraft({ ...EMPTY, mahallaId: draft.mahallaId, citizenLocale: draft.citizenLocale });
    setAttested(false);
    setError(null);
    setSubmissionId(crypto.randomUUID());
  }

  if (created) {
    return <Receipt created={created} onAnother={startAnother} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/murojaats" className="text-sm font-semibold text-brand-700 hover:underline">
          ← {t('admin.common.back')}
        </Link>
        <h1 className="text-xl font-bold text-brand-900">{t('admin.reception.title')}</h1>
      </div>

      <ErrorBanner message={error} />

      {/* A browser that cannot hash cannot evidence consent, so it cannot file.
          Said plainly and up front rather than as a failure at submit time. */}
      {!canHash ? <WarningBanner>{t('admin.reception.errorNoCrypto')}</WarningBanner> : null}

      <p className="text-sm text-ink-700">{t('admin.reception.intro')}</p>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[3fr_2fr]">
        <div className="flex flex-col gap-5">
          <Card title={t('admin.reception.citizenSection')}>
            <div className="flex flex-col gap-4">
              <Field label={t('admin.column.mahalla')}>
                <Select
                  value={draft.mahallaId}
                  disabled={mahallasLoading}
                  onChange={(event) => set('mahallaId', event.target.value)}
                >
                  <option value="">{t('admin.reception.mahallaPlaceholder')}</option>
                  {mahallas.map((mahalla) => (
                    <option key={mahalla.id} value={mahalla.id}>
                      {mahalla.name}
                    </option>
                  ))}
                </Select>
              </Field>

              {/*
                §14.5.4 step 1 — the CITIZEN's language, and the hint says so.
                It sits beside the identity fields rather than in a settings
                corner because it is a fact about the person, not a preference
                of the operator.
              */}
              <Field
                label={t('admin.reception.citizenLocale')}
                hint={t('admin.reception.citizenLocaleHint')}
              >
                <Select
                  value={draft.citizenLocale}
                  onChange={(event) => set('citizenLocale', event.target.value as Locale)}
                >
                  {LOCALES.map((value) => (
                    <option key={value} value={value}>
                      {t(`admin.locale.${value}`)}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label={t('admin.detail.authorName')}>
                <TextInput
                  value={draft.authorName}
                  maxLength={AUTHOR_NAME_MAX_CHARS}
                  autoComplete="off"
                  onChange={(event) => set('authorName', event.target.value)}
                />
              </Field>

              <Field label={t('admin.detail.authorAddress')} hint={t('admin.reception.addressHint')}>
                <TextInput
                  value={draft.authorAddress}
                  maxLength={AUTHOR_ADDRESS_MAX_CHARS}
                  autoComplete="off"
                  onChange={(event) => set('authorAddress', event.target.value)}
                />
              </Field>

              <Field
                label={t('admin.reception.phone')}
                hint={t('admin.reception.phoneHint')}
                error={
                  draft.phone.length > 0 && normalizedPhone === null
                    ? t('admin.reception.phoneInvalid')
                    : undefined
                }
              >
                <TextInput
                  value={draft.phone}
                  inputMode="tel"
                  autoComplete="off"
                  placeholder={`${PHONE_PREFIX} __ ___ __ __`}
                  onChange={(event) => set('phone', event.target.value)}
                />
              </Field>
            </div>
          </Card>

          <Card title={t('admin.reception.bodySection')}>
            <Field
              label={t('admin.detail.body')}
              hint={`${String(bodyLength)} / ${String(BODY_MIN_CHARS)}–${String(BODY_MAX_CHARS)}`}
            >
              <TextArea
                value={draft.body}
                maxLength={BODY_MAX_CHARS}
                className="min-h-48"
                placeholder={t('admin.reception.bodyPlaceholder')}
                onChange={(event) => set('body', event.target.value)}
              />
            </Field>
          </Card>
        </div>

        <div className="flex flex-col gap-5">
          {/*
            ===============================================================
            THE CONSENT BLOCK. THIS IS THE LEGAL HALF OF THE SCREEN.
            ===============================================================
            Rendered in the CITIZEN's locale, at `text-base` rather than the
            `text-sm` the rest of the panel uses, with a border that marks it
            as a distinct object to be turned toward somebody. The instruction
            to turn the screen is itself a line of copy, in the STAFF member's
            language, because it is an instruction to them.
          */}
          <Card title={t('admin.reception.consentSection')}>
            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold uppercase tracking-label text-ink-500">
                {t('admin.reception.turnToCitizen')}
              </p>
              <p
                lang={draft.citizenLocale}
                className="rounded-tile border-1 border-line-300 bg-surface-50 px-4 py-4 text-base leading-relaxed text-ink-900"
              >
                {consentText}
              </p>

              {/* §14.5.4 step 3 — the attestation, in the first person, as a
                  sentence the staff member is signing rather than a label on a
                  switch. */}
              <label className="flex cursor-pointer items-start gap-3 rounded-chip px-1 py-2 text-sm text-ink-900 hover:bg-surface-50">
                <input
                  type="checkbox"
                  checked={attested}
                  onChange={(event) => setAttested(event.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 accent-brand-600"
                />
                <span>{t('admin.reception.attest')}</span>
              </label>

              <p className="text-xs text-ink-400">
                {t('admin.reception.consentVersion', { version: CONSENT_VERSION })}
              </p>
            </div>
          </Card>

          <Card title={t('admin.reception.fileSection')}>
            <div className="flex flex-col gap-3">
              <Button disabled={!ready} onClick={() => void submit()}>
                {submitting ? t('admin.reception.submitting') : t('admin.reception.submit')}
              </Button>
              {/* Why the button is dead, said out loud. A disabled control with
                  no explanation is a staff member clicking it repeatedly. */}
              {!complete ? (
                <p className="text-xs text-ink-500">{t('admin.reception.incomplete')}</p>
              ) : !attested ? (
                <p className="text-xs font-medium text-gold-700">
                  {t('admin.reception.attestRequired')}
                </p>
              ) : null}
              <p className="text-xs text-ink-400">{t('admin.reception.channelNote')}</p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

/**
 * §14.5.4 step 5 — the receipt.
 *
 * The reference is the largest thing on the screen, and the print action is a
 * browser print of a fragment: no PDF service, no new infrastructure, no font
 * to embed. `print:` utilities hide the rest of the panel, so what comes out of
 * the printer is a slip with the reference, the date and the deadline — and
 * NOT the citizen's name, address, phone or the appeal text. A receipt is proof
 * of filing, and a slip carrying somebody's complaint is a document that gets
 * left on a counter.
 */
function Receipt({
  created,
  onAnother,
}: {
  created: Created;
  onAnother: () => void;
}): React.JSX.Element {
  const { t } = useI18n();

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <h1 className="text-xl font-bold text-brand-900">{t('admin.reception.successTitle')}</h1>
      </div>

      <Card className="print:border-0 print:shadow-none">
        <div className="flex flex-col items-center gap-4 py-6 text-center">
          <p className="text-sm text-ink-700">{t('admin.reception.referenceLabel')}</p>
          {/* Large, monospaced-by-tracking, selectable. A citizen reads this
              aloud over the phone later; a handler types it into the inbox. */}
          <p className="select-all text-4xl font-bold tracking-ref text-brand-900">
            {created.reference}
          </p>
          <dl className="flex flex-col gap-1 text-sm text-ink-700">
            <div className="flex gap-2">
              <dt className="font-semibold">{t('admin.column.received')}:</dt>
              <dd>{formatDateTime(created.received_at)}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-semibold">{t('admin.column.due')}:</dt>
              <dd>{formatDate(created.due_at)}</dd>
            </div>
          </dl>
          <p className="max-w-md text-sm text-ink-500">{t('admin.reception.successNote')}</p>
        </div>
      </Card>

      <div className="flex flex-wrap gap-3 print:hidden">
        <Button onClick={() => window.print()}>{t('admin.reception.print')}</Button>
        <Button tone="secondary" onClick={onAnother}>
          {t('admin.reception.another')}
        </Button>
        <Link
          to={`/murojaats/${created.murojaat_id}`}
          className="inline-flex items-center rounded-btn border-1 border-line-300 bg-surface-0 px-4 py-2 text-sm font-semibold text-brand-800 hover:bg-surface-100"
        >
          {t('admin.reception.openAppeal')}
        </Link>
      </div>
    </div>
  );
}
